/**
 * MercadoPago SDK script injection — SDK JS v2 by default, with a
 * build-time rollback to the deprecated v1 bundle (issue #354).
 *
 * Lives in shared/ (not the booking feature) so TenantProvider can preload
 * the SDK as soon as tenant resolution reports paymentProcessor
 * 'mercadopago', without a core → features import.
 *
 * A script tag that failed to load stays in the DOM looking identical to
 * one that is still downloading, which is how a single network blip used
 * to lock the whole SPA session out of the payment form (the old hook
 * skipped injection whenever the tag existed). The `data-mp-failed` marker
 * set by the error listener is what lets consumers tell the two apart and
 * replace dead tags.
 */

const MP_SDK_V2_URL = 'https://sdk.mercadopago.com/js/v2';
// Deprecated: MP added a runtime throw for TEST keys on 2026-07-03 and the
// bundle no longer receives maintenance. Kept only as the rollback target.
const MP_SDK_V1_URL = 'https://secure.mlstatic.com/sdk/javascript/v1/mercadopago.js';

export const MP_SCRIPT_ID = 'mercadopago-sdk-script';
export const MP_FAILED_ATTR = 'mpFailed';

/** Card token payload for v2 createCardToken (raw-data Core Methods mode). */
export interface MpV2CardTokenParams {
  cardNumber: string;
  cardholderName: string;
  cardExpirationMonth: string;
  cardExpirationYear: string;
  securityCode: string;
  identificationType?: string;
  identificationNumber?: string;
}

export interface MercadoPagoV2Instance {
  createCardToken: (params: MpV2CardTokenParams) => Promise<{ id: string }>;
  getPaymentMethods: (params: { bin: string }) => Promise<{ results: Array<{ id: string }> }>;
}

declare global {
  interface Window {
    /**
     * Set by the v2 SDK's security.js when `advancedFraudPrevention` is on: the device
     * fingerprint MercadoPago's antifraud scores a payment with. Absent under the v1
     * rollback and when the script is blocked (ad blockers, privacy browsers).
     */
    MP_DEVICE_SESSION_ID?: string;
    /** SDK JS v2 constructor (sdk.mercadopago.com/js/v2). */
    MercadoPago?: new (
      publicKey: string,
      options?: { advancedFraudPrevention?: boolean },
    ) => MercadoPagoV2Instance;
    /** Legacy v1 global — only present under the VITE_MP_SDK_V2=false rollback. */
    Mercadopago?: {
      setPublishableKey: (key: string) => void;
      clearSession: () => void;
      createToken: (
        form: HTMLFormElement,
        callback: (status: number, response: { id?: string; error?: string; cause?: unknown }) => void,
      ) => void;
      getPaymentMethod: (
        params: { bin: string },
        callback: (status: number, response: Array<{ id: string }>) => void,
      ) => void;
      key?: string;
    };
  }
}

/**
 * Build-time rollback flag: set VITE_MP_SDK_V2=false and rebuild to ship
 * against the deprecated v1 bundle again. Escape hatch for one release
 * after the v2 migration (issue #354) — remove once v2 has soaked in prod.
 * Read at call time (not module load) so tests can stub it per-case.
 */
export function isMpSdkV2Enabled(): boolean {
  // Tolerant parsing: an operator producing an emergency rollback build
  // must not be betrayed by 'FALSE', '0', or a trailing space.
  const raw = String(import.meta.env.VITE_MP_SDK_V2 ?? '').trim().toLowerCase();
  return raw !== 'false' && raw !== '0';
}

function mpSdkUrl(): string {
  return isMpSdkV2Enabled() ? MP_SDK_V2_URL : MP_SDK_V1_URL;
}

/**
 * Both SDKs assign their global synchronously on eval, so a defined global
 * means the script already executed. v2 exposes the `MercadoPago`
 * constructor; v1 exposes the `Mercadopago` singleton.
 */
export function isMpSdkLoaded(): boolean {
  return isMpSdkV2Enabled()
    ? typeof window.MercadoPago !== 'undefined'
    : typeof window.Mercadopago !== 'undefined';
}

/**
 * Injects the SDK script tag, replacing a previously failed one if present.
 * Returns the live element so callers can attach their own error listeners.
 *
 * `attempt` > 1 appends a cache-busting query param: if an intermediary
 * (carrier proxy, corporate cache) poisoned the cached response, retrying
 * the exact same URL would replay the same failure.
 */
export function ensureMercadoPagoScript(attempt = 1): HTMLScriptElement {
  let script = document.getElementById(MP_SCRIPT_ID) as HTMLScriptElement | null;

  if (script && script.dataset[MP_FAILED_ATTR] === '1') {
    script.remove();
    script = null;
  }

  if (!script) {
    const el = document.createElement('script');
    el.src = attempt > 1 ? `${mpSdkUrl()}?attempt=${attempt}` : mpSdkUrl();
    el.id = MP_SCRIPT_ID;
    el.async = true;
    el.addEventListener(
      'error',
      () => {
        el.dataset[MP_FAILED_ATTR] = '1';
      },
      { once: true },
    );
    document.body.appendChild(el);
    script = el;
  }

  return script;
}

/**
 * Removes the current script tag (dead or in-flight) so the next
 * ensureMercadoPagoScript call injects a fresh one.
 */
export function removeMercadoPagoScript(): void {
  document.getElementById(MP_SCRIPT_ID)?.remove();
}

/**
 * Fire-and-forget preload for MP tenants, called from TenantProvider.
 * Gives the SDK the whole booking flow to download instead of the
 * 15 seconds the payment step used to allow on slow mobile connections.
 * useMercadoPagoSDK owns retries and key configuration; this only warms
 * the fetch.
 */
export function preloadMercadoPagoSDK(): void {
  if (isMpSdkLoaded()) return;
  ensureMercadoPagoScript();
}

/**
 * The device session id the v2 SDK minted for this page (`window.MP_DEVICE_SESSION_ID`),
 * or null when there is none: the v1 rollback, a blocked security.js, or a value that is
 * not the opaque token the SDK sets. The backend sends it to MercadoPago as
 * `X-meli-session-id` (docs/plans/2026-10-21-mercadopago-approval-hardening.md, A4); a
 * null degrades to "not sent", never to an error.
 */
export function getMpDeviceSessionId(): string | null {
  return readMpDeviceSessionId().id;
}

/**
 * `sent`: the SDK set an id of the shape it documents; `none`: it set nothing (v1 rollback,
 * blocked security.js); `unrecognized`: it set something the shape check refused, which is
 * what MercadoPago changing the id's format would look like. Reported on the payment
 * breadcrumb so the two kinds of "not sent" stay distinguishable in Sentry.
 */
export type MpDeviceSessionIdState = 'sent' | 'none' | 'unrecognized';

export function readMpDeviceSessionId(): { id: string | null; state: MpDeviceSessionIdState } {
  const value = typeof window === 'undefined' ? undefined : window.MP_DEVICE_SESSION_ID;
  const trimmed = typeof value === 'string' ? value.trim() : '';
  if (trimmed === '') return { id: null, state: 'none' };
  return /^[A-Za-z0-9._-]{8,255}$/.test(trimmed) ? { id: trimmed, state: 'sent' } : { id: null, state: 'unrecognized' };
}
